import { createStore } from 'vuex';
import axios from 'axios';

const store = createStore({
  state: {
    user: null,
    instance: null,
    branch: null,
  },
  mutations: {
    setUser(state, userData) {
      state.user = userData;
    },
    clearUser(state) {
      state.user = null;
    },
    setInstance(state, instanceData) {
      state.instance = instanceData;
    },
    clearInstance(state) {
      state.instance = null;
    },
    setBranch(state, branchData) {
      state.branch = branchData;
    },
    clearBranch(state) {
      state.branch = null;
    },
  },
  actions: {
    async fetchUser({ commit, state }) {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Add instance and branch as custom headers if available
        if (state.instance?.domain) {
          headers['X-Instance-Domain'] = state.instance.domain;
        }
        if (state.branch?.url) {
          headers['X-Branch-Url'] = state.branch.url;
        }

        const res = await axios.get(`${process.env.VUE_APP_API_URL}/api/user`, { headers });
        commit('setUser', res.data);
      } catch (err) {
        console.warn('fetchUser failed, clearing token', err?.response?.status);
        commit('clearUser');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    },

    async fetchInstance({ commit }, instanceSubdomain) {
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
          query: `
            query($url: String!) {
              instanceByUrl(url: $url) {
                id
                name
                domain
                license_count
                branch_limit
                theme
                trial_ends_at
                is_active
                logo_url
              }
            }
          `,
          variables: { url: instanceSubdomain },
        });

        const instance = res.data.data.instanceByUrl;
        if (instance) {
          commit('setInstance', instance);
        } else {
          console.warn('No instance found for:', instanceSubdomain);
        }
      } catch (error) {
        console.error('Error fetching instance:', error);
      }
    },

    async fetchBranch({ commit }, branchSubdomain) {
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
          query: `
            query($url: String!) {
              branch(url: $url) {
                id
                name
                url
                logo_url
                primary_color
                secondary_color
              }
            }
          `,
          variables: { url: branchSubdomain },
        });

        const branch = res.data.data.branch;
        if (branch) {
          commit('setBranch', branch);
        } else {
          console.warn('No branch found for:', branchSubdomain);
        }
      } catch (error) {
        console.error('Error fetching branch:', error);
      }
    },

    clearUser({ commit }) {
      commit('clearUser');
      commit('clearInstance');
      commit('clearBranch');
      localStorage.removeItem('token');
    },
  },
  getters: {
    getUser: state => state.user,
    getInstance: state => state.instance,
    getBranch: state => state.branch,
    isAuthenticated: state => !!state.user,
    getInstanceId: state => state.instance?.id || null,
    getUserRole: state => state.user?.role || null,
    isSuperAdmin: state => state.user?.role === 'super_admin',
    isAdmin: state => state.user?.role === 'admin',
    isLearner: state => state.user?.role === 'learner',
    isInstructor: state => state.user?.role === 'instructor',
    isManager: state => state.user?.role === 'manager',
  },
});

export default store;
